import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useSession from '@/hooks/useSession';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Thread,
  ThreadID,
  ThreadItem,
  ThreadStatus,
  ThreadFilters,
  DEFAULT_FILTERS,
  WSParameters,
  ThreadProcessingStatus,
  PROMPT_APPROACHES,
} from '@/models/thread';

import { getToday } from '@/utils/functions';
import updateTitle from '@/api/threads/updateTitle';
import ThreadContext from './ThreadContext';
import useThreadWebSocket from '../hooks/useThreadWebSocket';
import useAsyncThreadUpdate from '../hooks/useAsyncThreadUpdate';
import useDeepDiveAutoSelect from '../hooks/useDeepDiveAutoSelect';
import { useSearchParams } from 'react-router-dom';

export const ThreadProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { id: routeParamID } = useParams();
  const [searchParams] = useSearchParams();

  const [threadID, setThreadID] = useState<ThreadID>(routeParamID);
  const { session, refreshThreads, setSession, pushUserEvent } = useSession();
  const { sub, threads } = session;
  const [title, setTitle] = useState<string>('');
  const [selectedApproach, setSelectedApproach] = useState<number>(0);
  const [items, setItems] = useState<ThreadItem[]>([]);
  const [filters, setFilters] = useState<ThreadFilters>(DEFAULT_FILTERS);
  const [status, setStatus] = useState<ThreadStatus>('READY');
  const promptRef = useRef<HTMLTextAreaElement>(null);

  const navigate = useNavigate();

  const processingStatus = useMemo(() => {
    const thread = threads.find((t) => t.id === threadID);
    if (thread) {
      return thread.processingStatus;
    }
    return undefined;
  }, [threadID, threads]);

  const updateThreadProcessing = useCallback(
    (newStatus: ThreadProcessingStatus | undefined) => {
      setSession((prev) => {
        const threads = [...prev.threads];
        const threadIndex = threads.findIndex(
          (thread) => thread.id === threadID
        );
        if (threadIndex !== -1) {
          threads[threadIndex].processingStatus = newStatus;
        }

        return { ...prev, threads };
      });
    },
    [setSession, threadID]
  );

  const { setShouldCheckAsync } = useAsyncThreadUpdate({
    threadID,
    setItems,
    routeParamID,
    processingStatus,
    updateThreadProcessing,
    setStatus,
  });

  const { sendWSMessage, setIdToken } = useThreadWebSocket({
    status,
    setStatus,
    items,
    setItems,
    updateThreadProcessing,
    threadID,
    setThreadID,
    setTitle,
    filters,
  });

  const { start: startDeepDiveAutoSelect, ...deepDiveAutoSelect } =
    useDeepDiveAutoSelect({ threadID, setSelectedApproach, pushUserEvent });
  const isDeepDive = PROMPT_APPROACHES[selectedApproach].id === 'deep-dive';

  const resetThread = useCallback(() => {
    setThreadID(undefined);
    setIdToken(undefined);
    setSelectedApproach(0);
    setTitle('');
    setItems([]);
    setStatus('READY');
    setShouldCheckAsync(false);
  }, [setIdToken, setShouldCheckAsync]);

  const handleTitleUpdate = (newTitle: string): void => {
    if (!threadID) return;

    const currentTitle = title;
    setTitle(newTitle);
    pushUserEvent('update_thread_title');
    updateTitle(sub, threadID, newTitle).then((success) => {
      if (success) {
        refreshThreads();
      } else {
        setTitle(currentTitle);
      }
    });
  };

  const handlePromptSubmit = (
    prompt: string,
    filters?: ThreadFilters
  ): void => {
    const newThreadItem: ThreadItem = {
      disliked: false,
      prompt,
      response: [],
      enabledCitation: true,
      queryDate: getToday(),
      deepDive: isDeepDive,
    };
    setItems([...items, newThreadItem]);

    if (!threadID) {
      setFilters(filters ?? DEFAULT_FILTERS);
      pushUserEvent('submit_new_thread');
      navigate('/thread/new');
    } else {
      pushUserEvent('add_follow_up');
      addResponse(newThreadItem);
    }
  };

  const handleCitationRequest = (item: ThreadItem): void => {
    if (item.id === 'new' || !item.id || !session.sub) return;
    setStatus('CITING');
    pushUserEvent('add_citation');
    sendWSMessage({
      action: 'generateCitation',
      user_sub: session.sub,
      thread_id: threadID,
      message_id: item.id,
    });
  };

  // Add Response
  const addResponse = useCallback(
    (item: ThreadItem): void => {
      setStatus('FETCHING');
      const { remainingThisMonth } = session.promptUsage;

      if (remainingThisMonth <= 0) {
        setStatus('ERROR');
        return;
      }

      const { earliestDate, latestDate } = filters;

      const wsParams: WSParameters = {
        action: 'createThreadPre',
        user_sub: session.sub,
        user_input: item.prompt,
      };

      if (!threadID) {
        wsParams.filters = {
          source_category: filters.sources,
          publish_date: {
            earliest: earliestDate ? new Date(earliestDate).getTime() : 1,
            latest: latestDate
              ? new Date(latestDate).getTime()
              : getToday().getTime(),
          },
          countries: filters.countries,
        };
      } else {
        wsParams.action = 'addMessageThreadPre';
        wsParams.thread_id = threadID;
      }

      if (isDeepDive) {
        wsParams.deep_dive = true;
      }
      sendWSMessage(wsParams);
    },
    [
      session.promptUsage,
      session.sub,
      filters,
      threadID,
      isDeepDive,
      sendWSMessage,
    ]
  );

  // Update ThreadID
  useEffect(() => {
    if (routeParamID !== 'new' && routeParamID !== threadID) {
      setThreadID(routeParamID);
      setItems([]);
    }
  }, [routeParamID, threadID]);

  // Start New Thread
  useEffect(() => {
    if (
      routeParamID === 'new' &&
      items.length === 1 &&
      items[0].response.length === 0 &&
      status === 'READY'
    ) {
      addResponse(items[items.length - 1]);
    }
  }, [addResponse, items, routeParamID, searchParams, status]);

  // Load Existing Thread
  useEffect(() => {
    if (
      items.length === 0 &&
      typeof threadID !== 'undefined' &&
      routeParamID === threadID
    ) {
      const savedThread = threads.find(
        (thread: Thread) => thread.id === threadID
      );
      if (savedThread) {
        setTitle(savedThread.title);
        setItems(savedThread.items);
        setFilters(savedThread.filters);
        setIdToken(undefined);
        updateThreadProcessing(savedThread.processingStatus);

        if (savedThread.processingStatus) {
          setShouldCheckAsync(true);
          setStatus('FETCHING');
        } else {
          setStatus('READY');
        }

        // Pick up where the user left off: Deep Dive if the last request used it
        const lastItem = savedThread.items[savedThread.items.length - 1];
        if (lastItem?.deepDive && !isDeepDive) {
          startDeepDiveAutoSelect(threadID);
        }
      } else {
        navigate('/not-found', { replace: true });
      }
    }
  }, [
    navigate,
    routeParamID,
    threadID,
    threads,
    items,
    updateThreadProcessing,
    setIdToken,
    setShouldCheckAsync,
    isDeepDive,
    startDeepDiveAutoSelect,
  ]);

  // Update URL on new ThreadID
  useEffect(() => {
    if (routeParamID === 'new' && threadID) {
      navigate(`/thread/${threadID}`, { replace: true });
    }
  }, [navigate, routeParamID, threadID]);

  // Clean up when not in a thread
  useEffect(() => {
    if (typeof routeParamID === 'undefined') {
      resetThread();
    }
  }, [resetThread, routeParamID]);

  return (
    <ThreadContext.Provider
      value={{
        id: threadID,
        processingStatus,
        title,
        selectedApproach,
        setSelectedApproach,
        deepDiveAutoSelect,
        promptRef,
        items,
        status,
        filters,
        setStatus,
        handleTitleUpdate,
        handlePromptSubmit,
        handleCitationRequest,
        resetThread,
      }}
    >
      {children}
    </ThreadContext.Provider>
  );
};
